import { ajvResolver } from '@hookform/resolvers/ajv'
import { ArrowBack, Close, Done, ErrorOutlineOutlined } from '@mui/icons-material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Box, Button, CircularProgress, Grid, IconButton, Link, TextField } from '@mui/material'
import logoImage from 'assets/logo-conducco.svg'
import ImageBanner from 'components/image-banner'
import LogoTheme from 'components/logo-theme'
import AllDoneSVG from 'components/svg/all-done'
import PasswordBannerSVG from 'components/svg/password-banner'
import WrapperPermission from 'components/wrapper-permission'
import { useAlert } from 'contexts/alert'
import { useCustomTheme } from 'contexts/theme'
import React, { useCallback, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import ResetPasswordSchema, { IResetPasswordFormData } from 'schema/authentication/reset-password'
import api from 'services/api'
import { useStyles } from './styles'

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isAllDone, setIsAllDone] = useState<Boolean>(false)
  const [isPasswordHidden, setIsPasswordHidden] = useState<Boolean>(true)
  const [isConfirmPasswordHidden, setIsConfirmPasswordHidden] = useState<Boolean>(true)
  const [isHidePasswordRequirements, setIsHidePasswordRequirements] = useState(false)

  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { theme } = useCustomTheme()
  const { closeAlert, openAlert } = useAlert()

  // const validationSchema = yup.object().shape({
  //   password: yup
  //     .string()
  //     .required('Senha obrigatória')
  //     .min(8, 'Senha deve ter no mínimo 8 caracteres.')
  //     .matches(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula.')
  //     .matches(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula.')
  //     .matches(/\d/, 'Senha deve conter pelo menos um número.')
  //     .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Senha deve conter pelo menos um caractere especial.'),
  //   password_confirmation: yup.string().oneOf([yup.ref('password'), null], 'Confirmação incorreta'),
  // })

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IResetPasswordFormData>({
    resolver: ajvResolver(ResetPasswordSchema),
  })

  const passwordString = watch('password')
  const letraMinusculaRegex: RegExp = /[a-z]/
  const letraMaiusculaRegex: RegExp = /[A-Z]/
  const umNumeroRegex: RegExp = /\d/
  const caracterEspcial: RegExp = /[!@#$%^&*(),.?":{}|<>]/

  const onSubmit: SubmitHandler<IResetPasswordFormData> = useCallback(
    async (data) => {
      try {
        setIsLoading(true)

        const { password, password_confirmation } = data
        const token = location.search.replace('?token=', '')

        if (!token) {
          throw new Error()
        }

        await api.post('/passwords/reset', {
          password,
          passwordConfirmation: password_confirmation,
          token,
        })

        setIsLoading(false)
        setIsAllDone(true)
      } catch (err) {
        setIsLoading(false)
        openAlert('Erro ao resetar a senha!', 'error')
      }
    },
    [location.search, history]
  )

  const handleChange = (formField: any) => closeAlert()

  const PassWordValidation: React.FC = () => {
    return (
      <Box
        position='absolute'
        width='100%'
        height='180px'
        bgcolor='white'
        zIndex='10'
        boxShadow='2'
        display='flex'
        alignItems='flex-start'
        justifyContent='center'
        flexDirection='column'
        padding='16px 32px'
        gap='8px'
      >
        <Box
          display='flex'
          gap='8px'
          sx={{
            color: passwordString.length <= 0 ? 'black' : letraMinusculaRegex.test(passwordString) ? 'green' : 'red',
          }}
        >
          {passwordString.length <= 0 ? (
            <ErrorOutlineOutlined />
          ) : letraMinusculaRegex.test(passwordString) ? (
            <Done color='success' />
          ) : (
            <Close color='error' />
          )}
          <span>Um caractere minúsculo</span>
        </Box>

        <Box
          display='flex'
          gap='8px'
          sx={{
            color: passwordString.length <= 0 ? 'black' : letraMaiusculaRegex.test(passwordString) ? 'green' : 'red',
          }}
        >
          {passwordString.length <= 0 ? (
            <ErrorOutlineOutlined />
          ) : letraMaiusculaRegex.test(passwordString) ? (
            <Done color='success' />
          ) : (
            <Close color='error' />
          )}
          <span>Um caractere maiúsculo</span>
        </Box>

        <Box
          display='flex'
          gap='8px'
          sx={{
            color: passwordString.length <= 0 ? 'black' : umNumeroRegex.test(passwordString) ? 'green' : 'red',
          }}
        >
          {passwordString.length <= 0 ? (
            <ErrorOutlineOutlined />
          ) : umNumeroRegex.test(passwordString) ? (
            <Done color='success' />
          ) : (
            <Close color='error' />
          )}
          <span>Um número</span>
        </Box>

        <Box
          display='flex'
          gap='8px'
          sx={{
            color: passwordString.length <= 0 ? 'black' : caracterEspcial.test(passwordString) ? 'green' : 'red',
          }}
        >
          {passwordString.length <= 0 ? (
            <ErrorOutlineOutlined />
          ) : caracterEspcial.test(passwordString) ? (
            <Done color='success' />
          ) : (
            <Close color='error' />
          )}
          <span>Um caractere especial</span>
        </Box>

        <Box
          display='flex'
          gap='8px'
          sx={{
            color: passwordString.length <= 0 ? 'black' : passwordString.length >= 8 ? 'green' : 'red',
          }}
        >
          {passwordString.length <= 0 ? (
            <ErrorOutlineOutlined />
          ) : passwordString.length >= 8 ? (
            <Done color='success' />
          ) : (
            <Close color='error' />
          )}
          <span>Mínimo de 8 caracteres</span>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.mainWrapper}>
        <WrapperPermission>
          {!isAllDone ? (
            <Box
              component='form'
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              data-testid='form'
              className={classes.formBox}
            >
              <LogoTheme />

              <h1 className={classes.formTitle}>Definir nova senha</h1>
              <span className={classes.formSubTitle}>
                Por favor, escolha uma nova senha para sua conta. Sua senha deve conter no mínimo 8 caracteres e incluir
                pelo menos uma letra maiúscula, uma letra minúscula, um número e um caractere especial.
              </span>

              <Box
                position='relative'
                width='100%'
              >
                <TextField
                  required
                  autoFocus
                  id='password'
                  label='Senha'
                  variant='outlined'
                  autoComplete='new-password'
                  placeholder='Escolha uma senha'
                  error={Boolean(errors.password)}
                  type={isPasswordHidden ? 'password' : 'text'}
                  style={{ marginBottom: '10px', marginTop: '10px' }}
                  onFocus={() => setIsHidePasswordRequirements(true)}
                  onBlurCapture={() => setIsHidePasswordRequirements(false)}
                  helperText={errors?.password?.message ?? ''}
                  InputProps={{
                    startAdornment: (
                      <LockOutlinedIcon
                        color={isLoading ? 'disabled' : 'inherit'}
                        sx={{ marginRight: '12px' }}
                      />
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                        disabled={isLoading}
                      >
                        {isPasswordHidden ? (
                          <VisibilityOffOutlinedIcon color={isLoading ? 'disabled' : 'inherit'} />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    ),
                    sx: { paddingRight: '8px' },
                  }}
                  {...register('password', { onChange: (e) => handleChange(e) })}
                />
                {isHidePasswordRequirements && <PassWordValidation />}
              </Box>

              <TextField
                required
                id='password_confirmation'
                label='Confirme sua senha'
                autoComplete='new-password'
                variant='outlined'
                placeholder='Escolha uma senha'
                error={Boolean(errors.password_confirmation)}
                type={isConfirmPasswordHidden ? 'password' : 'text'}
                style={{ marginBottom: '10px', marginTop: '10px' }}
                helperText={errors?.password_confirmation?.message ?? ''}
                InputProps={{
                  startAdornment: (
                    <LockOutlinedIcon
                      color={isLoading ? 'disabled' : 'inherit'}
                      sx={{ marginRight: '12px' }}
                    />
                  ),
                  endAdornment: (
                    <IconButton
                      onClick={() => setIsConfirmPasswordHidden(!isConfirmPasswordHidden)}
                      disabled={isLoading}
                    >
                      {isConfirmPasswordHidden ? (
                        <VisibilityOffOutlinedIcon color={isLoading ? 'disabled' : 'inherit'} />
                      ) : (
                        <VisibilityOutlinedIcon />
                      )}
                    </IconButton>
                  ),
                  sx: { paddingRight: '8px' },
                }}
                {...register('password_confirmation', { onChange: (e) => handleChange(e) })}
              />

              <Button
                fullWidth
                size='large'
                type='submit'
                variant='contained'
                sx={{ mt: 2, mb: 1 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress
                    color='inherit'
                    size={26}
                  />
                ) : (
                  'REDEFINIR SENHA'
                )}
              </Button>

              <Grid container>
                <Grid
                  item
                  xs={12}
                >
                  <Link
                    href='/'
                    variant='inherit'
                    data-testid='signup-link'
                    className={classes.signupLink}
                  >
                    <ArrowBack
                      color='inherit'
                      sx={{ marginRight: '8px' }}
                    />
                    Voltar para a página de Login
                  </Link>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box className={classes.formBox}>
              <img
                src={logoImage}
                className={classes.logoImage}
                alt='Logo'
              />

              <h1 className={classes.formTitle}>Tudo feito!</h1>
              <span className={classes.formSubTitle}>
                Sua senha foi redefinida e agora você pode fazer login na sua conta.
              </span>

              <Button
                fullWidth
                size='large'
                type='submit'
                variant='contained'
                sx={{ mt: 2, mb: 1 }}
                onClick={() => history.push('/')}
              >
                LOGIN
              </Button>
            </Box>
          )}
        </WrapperPermission>

        <ImageBanner
          title='Crie uma nova senha para sua conta na Conducco.'
          subTitle='Conte com a tranquilidade de saber que suas informações estão protegidas e acessíveis apenas a você e aos colaboradores autorizados. Faça login agora e mantenha seus dados seguros.'
          banner={isAllDone ? <AllDoneSVG /> : <PasswordBannerSVG />}
        />
      </Box>
    </Box>
  )
}

export default ResetPassword
